<template>
  <div class="home">
    <div class="home-banner">
      <div class="container">
        <h1 class="home-banner__title">Actinobacteria TFDB</h1>
        <p class="home-banner__desc">
          Welcome to the Transcription Factors website, where you can search for
          transcription factors, transcription regulatory ligands (TF-RLs), and
          use analysis tools.
        </p>
        <div class="home-banner__search">
          <div class="search-box">
            <input
              v-model="searchInput"
              class="search-box__input"
              placeholder="Input TF name, Species, Ensembl ID, UniProt ID or any other keyword."
              type="text"
              @keyup.enter="handleKeyupEnter"
            />
            <button class="search-box__btn" @click="handleSearch">
              <i class="el-icon el-icon-search"></i>
            </button>
          </div>
          <div class="home-banner__search-advanced">
            <button
              :class="{
                'home-banner__search__btn__active': true,
              }"
              class="home-banner__search__btn"
              @click="handleSearch"
            >
              Advanced Search
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="home-introduce">
      <div class="container">
        <h3 class="home-introduce__title">Introduce</h3>
        <div class="home-introduce__content">
          <div class="home-introduce__graph">
            <div
              class="home-introduce__graph-item home-introduce__graph-center"
            >
              <span>TFs</span>
              <span>{{ statistics["TFs"] }} </span>
            </div>
            <div class="home-introduce__graph-item home-introduce__graph-top">
              <span>TF Targets</span>
              <span> {{ statistics["TF Targets"] }} </span>
            </div>
            <div
              class="home-introduce__graph-item home-introduce__graph-top-left"
            >
              <span>TF Families</span>
              <span>{{ statistics["TF Families"] }}</span>
            </div>
            <!--<div
              class="home-introduce__graph-item home-introduce__graph-bottom-left"
            >
              <span>TF Cof Families</span>
              <span> {{ statistics["TF Cof Families"] }} </span>
            </div>-->

            <div
              class="home-introduce__graph-item home-introduce__graph-bottom"
            >
              <span>TF Regulatory Ligands</span>
              <span>{{ statistics["TF Cofactors"] }} </span>
            </div>
            <div
              class="home-introduce__graph-item home-introduce__graph-bottom-right"
            >
              <span>Species</span>
              <span>{{ statistics["Species"] }} </span>
            </div>

            <div
              class="home-introduce__graph-item home-introduce__graph-top-right"
            >
              <span>TF posttranslational modification sites</span>
              <span>
                {{ statistics["TF posttranslational modification sites"] }}
              </span>
            </div>
          </div>
          <div class="home-introduce__desc">
            <div class="home-introduce__desc-welcome">
              <h3 class="home-introduce__title">
                Welcome to Actinobacteria TFDB
              </h3>
              <p class="home-introduce__desc-text">
                Transcription factors (TFs) are DNA&#8209;binding proteins that
                <b>regulate transcriptional initiation</b> by targeting specific
                elements and coordinating with regulatory ligands. These
                proteins govern bidirectional control (activation/repression) of
                genes critical to differentiation, development, and stress
                adaptation. Their modular DNA&#8209;binding domains and
                interaction interfaces mediate transcriptional reprogramming of
                cellular phenotypes.
              </p>
              <p class="home-introduce__desc-text">
                <b>The Actinobacteria TFDB</b> systematically annotates
                regulatory networks across transcriptional interactions, genomic
                paralog/ortholog relationships, TF binding sites,
                pathway&#8209;phenotype associations, GO terms, and
                post&#8209;translational modifications. The platform emphasizes
                evolutionary conservation of TF regulatory ligands networks and
                integrates multi&#8209;omics data with predictive regulatory
                circuit modeling, serving as a resource for mechanistic studies
                in Actinobacteria.
              </p>
            </div>
            <div class="home-introduce__desc-citation">
              <h3 class="home-introduce__title">Citation</h3>
              <p class="home-introduce__desc-text">
                If you use <b>Actinobacteria TFDB web resources</b> to assist in
                research publications or proposals, please cite as:ATF:
                Actinobacteria TFDB.2023.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { countingForAll } from "@/api/home";

export default {
  name: "HomeView",
  data() {
    return {
      searchInput: "",
      statistics: {
        "TF Families": 0,
        "TF Targets": 0,
        TFs: 0,
        "TF Cof Families": 0,
        "TF Cofactors": 0,
        Species: 0,
        "TF posttranslational modification sites": 0,
      },
    };
  },
  mounted() {
    countingForAll().then((res) => {
      this.$nextTick(() => {
        this.statistics = {
          "TF Families": res.data.tfFamily,
          "TF Categories": res.data.tfCategories,
          TFs: res.data.tfs,
          "TF Cof Families": res.data.tfFamily,
          "TF Cof Categories": res.data.tfCofCategories,
          "TF Cofactors": res.data.tfCofactors,
          Species: res.data.species,
          "TF posttranslational modification sites":
            res.data["tfPost-translationalModificationSites"],
        };
        this.statistics = {
          "TF Families": 69,
          "TF Targets": 11776,
          TFs: 629,
          "TF Cof Families": 55,
          "TF Cofactors": 54,
          Species: 25,
          "TF posttranslational modification sites": 28,
        };
      });
    });
  },
  methods: {
    handleKeyupEnter() {
      if (this.searchInput.trim()) {
        this.handleSearch();
      }
    },
    handleSearch() {
      this.$router.push({
        name: "Search",
        query: {
          search: this.searchInput.trim(),
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.home {
  .home-banner {
    display: flex;
    align-items: center;
    height: px2rem(500);
    background-image: url("~@/assets/home/banner.webp");
    background-size: cover;
    background-position: bottom center;
  }

  .home-banner__title {
    font-size: px2rem(48);
    margin-bottom: px2rem(40);
  }

  .home-banner__desc {
    width: px2rem(884);
    font-size: px2rem(18);
    line-height: 1.5;
    margin-bottom: px2rem(70);
  }

  .home-banner__search {
    width: px2rem(715);
  }

  .home-banner__search-advanced {
    display: flex;
    flex-direction: row-reverse;
  }

  .home-banner__search__btn {
    background: #6a98ff;
    width: px2rem(153);
    height: px2rem(40);
    font-size: px2rem(14);
    color: #fff;
    margin-top: px2rem(20);
    border-radius: px2rem(6);

    &__active {
      background: $--color-primary;
    }

    &__disabled {
      cursor: not-allowed;
    }
  }

  .home-introduce {
    padding-top: px2rem(54);
    padding-bottom: px2rem(130);
  }

  .home-introduce__title {
    font-size: px2rem(20);
    font-weight: bold;
    color: #fff;
    background-color: $--color-primary;
    padding: px2rem(20);
    border-radius: px2rem(6);
  }

  .home-introduce__content {
    display: flex;
    padding-top: px2rem(40);
  }

  .home-introduce__graph {
    position: relative;
    width: px2rem(520);
    height: px2rem(520);
    margin-right: px2rem(36);
    border-radius: 50%;
    border: px2rem(1) solid $--color-primary;
  }

  .home-introduce__graph-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: absolute;
    width: px2rem(126);
    height: px2rem(126);
    border-radius: 50%;
    font-size: px2rem(16);
    color: #fff;
    text-align: center;

    span {
      line-height: 1.5;
    }
  }

  .home-introduce__graph-center {
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border: px2rem(1) solid $--color-primary;
    color: $--color-primary;
    font-weight: bold;
    font-size: px2rem(20);
  }

  .home-introduce__graph-top {
    top: 20%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: $--color-primary;
  }

  $step: 0.08;

  .home-introduce__graph-top-left {
    left: 20%;
    top: 46%;
    transform: translate(-50%, -50%);
    background-color: fade-out($--color-primary, 0.1);
  }

  .home-introduce__graph-left {
    left: 20%;
    top: 50%;
    transform: translate(-50%, -50%);
    background-color: fade-out($--color-primary, 0.1 + $step);
  }

  .home-introduce__graph-bottom-left {
    top: 66%;
    left: 24%;
    transform: translate(-50%, -50%);
    background-color: fade-out($--color-primary, 0.1 + $step * 2);
  }

  .home-introduce__graph-bottom {
    top: 76%;
    left: 33%;
    transform: translate(-50%, -50%);
    background-color: fade-out($--color-primary, 0.1 + $step * 3);
  }

  .home-introduce__graph-bottom-right {
    left: 68%;
    top: 76%;
    transform: translate(-50%, -50%);
    background-color: fade-out($--color-primary, 0.1 + $step * 4);
  }

  .home-introduce__graph-right {
    left: 80%;
    top: 50%;
    transform: translate(-50%, -50%);
    background-color: fade-out($--color-primary, 0.1 + $step * 5);
  }

  .home-introduce__graph-top-right {
    left: 80%;
    top: 46%;
    transform: translate(-50%, -50%);
    background-color: fade-out($--color-primary, 0.1 + $step * 6);
  }

  .home-introduce__desc {
    flex: 1;
  }

  .home-introduce__desc-welcome,
  .home-introduce__desc-citation {
    .home-introduce__title {
      font-size: px2rem(18);
      background-color: $--color-secondary;
      margin-bottom: px2rem(30);
    }
  }

  .home-introduce__desc-text {
    font-size: px2rem(14);
    line-height: 1.5;
    margin-bottom: px2rem(30);
    padding-left: px2rem(20);
    word-break: keep-all; /* 不允许在单词内断行 */
    word-wrap: normal; /* 正常换行，不拆分单词 */
    overflow-wrap: normal; /* 替代原来的 break-word */
    //overflow-wrap: break-word;
    // hyphens: auto;
    text-align: justify; /* 两边对齐 */
  }

  .home-tools {
    background-color: #f5f8ff;
    padding-top: px2rem(54);
    padding-bottom: px2rem(70);
    margin-bottom: px2rem(130);
  }

  .home-tools__header {
    font-size: px2rem(20);
    font-weight: bold;
    position: relative;
    text-align: center;
    margin-bottom: px2rem(80);

    &::after {
      position: absolute;
      bottom: px2rem(-20);
      left: 50%;
      transform: translateX(-50%);
      content: "";
      display: block;
      width: px2rem(38);
      height: px2rem(6);
      border-radius: px2rem(6);
      background-color: $--color-secondary;
      box-shadow: 0 px2rem(5) px2rem(10) 0 rgba(0, 0, 0, 0.1);
    }
  }

  .home-tools__list {
    display: flex;
    justify-content: space-between;
  }

  .home-tools__item {
    width: 33.1%;
    height: px2rem(240);
    background-color: #fff;
    border-radius: px2rem(6);
    padding: px2rem(30) px2rem(40);
  }

  .home-tools__img {
    margin-bottom: px2rem(20);

    &.home-tools__img__first {
      width: px2rem(48);
      height: px2rem(60);
    }

    &.home-tools__img__second {
      width: px2rem(60);
      height: px2rem(60);
    }

    &.home-tools__img__third {
      width: px2rem(60);
      height: px2rem(60);
    }
  }

  .home-tools__title {
    font-size: px2rem(16);
    margin-bottom: px2rem(32);
  }

  .home-tools__desc {
    font-size: px2rem(14);
    color: $--color-text-secondary;
  }
}
</style>

<style lang="scss">
.search-box {
  display: flex;
  height: px2rem(50);
  background-color: #fff;
  border-radius: px2rem(4);
  padding: px2rem(14) px2rem(10) px2rem(14) px2rem(14);

  .search-box__input {
    font-size: px2rem(14);
    border: none;
    height: 100%;
    flex: 1;
  }

  .search-box__btn {
    color: #999;
    background-color: transparent;
    border: none;
  }
}
</style>
